import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import { SectionHeading } from "components/misc/Headings.js";
import { Link } from "react-router-dom";

const HeaderBar = tw.div`w-full flex items-center justify-between`;
const Header = styled(SectionHeading)`
  width: auto;
  text-align: left;
  font-size: 22px !important;
  font-weight: 600;
  font-family: 'Manrope', sans-serif !important;
  line-height: 1.2;
  margin-left: 0;
`;
const ViewMoreButton = styled(Link)`
  ${tw`font-bold rounded text-gray-100`}
  background-color: #FFFFFF;
  color:#000000;
  border:1px solid #000000;
  padding: 10px 14px;
  transition: background-color .2s ease;
  &:hover { background-color: #000000;color: #FFFFFF; }
`;
const TabContent = tw(motion.div)`mt-6 flex flex-wrap sm:-mr-10 md:-mr-6 lg:-mr-12`;
const CardContainer = tw.div`mt-10 w-full sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/4 sm:pr-6 lg:pr-8`;
const Card = tw(motion.div)`rounded-2xl block max-w-xs mx-auto sm:max-w-none sm:mx-0 mb-0 bg-white`;
const ImageBox = styled.div`
  ${tw`relative rounded-2xl`}
  height: 260px;
  width: 100%;
  border-radius: 16px;
  overflow: hidden; /* clip image corners */
  box-shadow: 0 6px 12px rgba(0,0,0,0.06);
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover; /* fill box nicely */
  border-radius: 16px; /* ensure rounded corners on the image itself */
  display: block;
`;

const HeartBtn = styled.button`
  ${tw`absolute rounded-full flex items-center justify-center`}
  top: 12px;
  right: 12px;
  width: 36px; height: 36px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0,0,0,0.08);
  border: 0;
  z-index: 2;
`;
const QuickView = styled.span`
  ${tw`absolute rounded-full text-sm`}
  bottom: 12px;
  right: 12px;
  padding: 6px 12px;
  background: #ffffff;
  color: #edbf03;
  box-shadow: 0 6px 12px rgba(0,0,0,0.08);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  z-index: 2;
`;
// Card action components are currently unused
const CardText = tw.div`p-4 text-gray-900`;
const CardTitle = tw.h5`
  text-base font-semibold text-[#0b2f2f]
`;
const SubRow = styled.div`
  ${tw`flex items-center justify-between mt-2`}
`;
const Mg = tw.span`text-xs text-gray-500`;
const Stars = styled.div`
  ${tw`text-sm mt-2`}
  color: #f59e0b;
`;
const Price = styled.div`
  ${tw`mt-1`}
  color: #000000;
  font-weight: 700;
  font-size: 14px;
`;
const CTA = styled.div`
  ${tw`mt-3`}
`;
const ShopButton = styled.div`
  ${tw`w-full text-center font-semibold text-white rounded-full`}
  background: #000000;
  padding: 12px 16px;
`;

const ProductTabs = ({ heading = "Nos produits", products = [], viewMoreHref }) => {
  const { t, i18n } = useTranslation();
  // Determine role for pricing (supplier/influenceur see distributor price)
  const role = typeof window !== 'undefined' ? localStorage.getItem('role') : null;
  console.log("ProductTabs products:", products);
  // Fonction pour générer un slug SEO-friendly (robuste aux valeurs undefined/objets)
  const slugify = (text) => {
    let raw = '';
    if (typeof text === 'string' || typeof text === 'number') {
      raw = String(text);
    } else if (text && typeof text === 'object') {
      // Essayer des clés courantes quand on reçoit un objet de traduction
      raw = text.fr || text.ar || text.en || text.name || text.label || '';
    }
    return String(raw)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
  .replace(/[^\w\u0600-\u06FF-]+/g, '')
  .replace(/--+/g, '-');
  };

  // Parse JSON content if backend sends serialized localization
  const parseIfJson = (val) => {
    if (typeof val !== 'string') return val;
    const s = val.trim();
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
      try { return JSON.parse(s); } catch { return val; }
    }
    return val;
  };
  return (
    <Container style={{ marginTop: "-50px" }}>
      <ContentWithPaddingXl>
        <HeaderBar>
          <Header>{t(heading)}</Header>
          {viewMoreHref ? (
            <ViewMoreButton to={viewMoreHref}>{i18n.language === 'ar' ? 'عرض المزيد' : t('Voir_plus')}</ViewMoreButton>
          ) : (
            <div />
          )}
        </HeaderBar>
        <br />

        <TabContent>
          {products.length > 0 ? (
            products.map((card) => {
              // Normalize image URL: accept full URLs or backend storage paths
              const rawImage = card?.image || card?.Image || card?.image_path || '';
              let imageUrl = '';
              if (rawImage) {
                imageUrl = String(rawImage);
                if (!/^https?:\/\//i.test(imageUrl)) {
                  imageUrl = `https://jolyaparis.cloud/storage/${imageUrl}`.replace(/\\/g, '/');
                }
                try { imageUrl = encodeURI(imageUrl); } catch (e) { /* fallback to raw */ }
              } else {
                imageUrl = '/images/placeholder.png';
              }
              // Normaliser les accès pour éviter les erreurs quand Name est manquant
              const rawNameCandidate = card?.Name ?? card?.name ?? card?.ProductName ?? card?.Nom ?? '';
              const parsedName = parseIfJson(rawNameCandidate);
              const fr = (parsedName && typeof parsedName === 'object') ? (parsedName.fr || '') : (typeof parsedName === 'string' ? parsedName : '');
              const ar = (parsedName && typeof parsedName === 'object') ? (parsedName.ar || '') : '';
              // Choisir le nom selon la langue active, avec fallbacks raisonnables
              const nameToShow = i18n.language === 'ar'
                ? (ar || fr || 'منتج')
                : (fr || ar || 'Produit');

              const productNameForSlug = nameToShow || fr || ar || 'produit';

              return (
                <CardContainer key={card.ProductID || card.ID || card.id}>
                  <Link
                    to={`/product-details/${(card.ProductID || card.ID || card.id)}-${slugify(
                      productNameForSlug
                    )}`}
                  >
                    <Card
                      className="group"
                      initial="rest"
                      whileHover="hover"
                      animate="rest"
                    >
                      <ImageBox>
                        <ProductImage src={imageUrl} alt={nameToShow} />
                        <HeartBtn title="Wishlist" aria-label="wishlist">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>
                        </HeartBtn>
                        <QuickView>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          {i18n.language === 'ar' ? 'نظرة سريعة' : t('quick_view', { defaultValue: 'Aperçu rapide' })}
                        </QuickView>
                      </ImageBox>
                      <CardText>
                        <SubRow>
                          <CardTitle>{nameToShow}</CardTitle>
                          { (card.mg || card.Mg || card.mg_value) && <Mg>Mg {card.mg || card.Mg || card.mg_value}</Mg> }
                        </SubRow>
                   
                        <Stars>{"★★★★★"}</Stars>
                             {(() => {
                          const userP = card.user_price ?? card.userPrice ?? card.price ?? card.Price;
                          const distP = card.distributeur_price ?? card.distributor_price ?? card.wholesale_price;
                          const display = (role === 'supplier' || role === 'influenceur') ? (distP ?? userP) : userP;
                          return display ? <Price>{display} {t('currency')}</Price> : null;
                        })()}
                        <CTA>
                          <ShopButton>{i18n.language === 'ar' ? 'تسوق الآن →' : t('shop_now', { defaultValue: 'Acheter →' })}</ShopButton>
                        </CTA>
                      </CardText>
                    </Card>
                  </Link>
                </CardContainer>
              );
            })
          ) : (
                        <p>{t('loadingAvailableProducts')}</p>
          )}
        </TabContent>
      </ContentWithPaddingXl>

    </Container>
  );
};

export default ProductTabs;
